import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, MapPin, Calendar, Tag, Phone, ChevronLeft, ChevronRight, MessageCircle, Copy, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WhatsAppIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function CouponDetails() {
  const { couponId } = useParams();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [claimData, setClaimData] = useState(null);
  const [showWhatsAppCapture, setShowWhatsAppCapture] = useState(false);
  const [captureWhatsApp, setCaptureWhatsApp] = useState('');
  const [savingWhatsApp, setSavingWhatsApp] = useState(false);

  useEffect(() => { fetchData(); }, [couponId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [couponRes, userRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/coupons/${couponId}`, { headers }),
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers }),
      ]);
      setCoupon(couponRes.data);
      setUser(userRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erro ao carregar cupom');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!user?.whatsapp) {
      setShowWhatsAppCapture(true);
      return;
    }
    executeClaim();
  };

  const executeClaim = async () => {
    setClaiming(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/coupons/${couponId}/claim`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClaimData(response.data);
      setShowWhatsAppDialog(true);
      toast.success('Acesse o WhatsApp do lojista!');
    } catch (error) {
      console.error('Failed to claim coupon:', error);
      const message = error.response?.data?.detail || 'Erro ao ativar cupom';
      toast.error(message);
    } finally {
      setClaiming(false);
    }
  };

  const handleSaveWhatsApp = async () => {
    if (!captureWhatsApp.trim()) {
      toast.error('Digite seu numero de WhatsApp');
      return;
    }
    setSavingWhatsApp(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${BACKEND_URL}/api/auth/whatsapp`, { whatsapp: captureWhatsApp }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(prev => ({ ...prev, whatsapp: captureWhatsApp }));
      setShowWhatsAppCapture(false);
      toast.success('WhatsApp cadastrado!');
      executeClaim();
    } catch (error) {
      toast.error('Erro ao salvar WhatsApp');
    } finally {
      setSavingWhatsApp(false);
    }
  };

  const handleOpenWhatsApp = () => {
    if (!claimData || !coupon) return;
    const phoneNumber = coupon.merchant_whatsapp.replace(/\D/g, '');
    const message = `Ola! Quero usar o cupom de desconto do ClubdoCupom

*${coupon.title}*
Desconto: *${coupon.discount_percentage}%*
Codigo: *${claimData.code}*

Valido ate: ${new Date(claimData.expires_at).toLocaleDateString('pt-BR')}`;
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    setShowWhatsAppDialog(false);
    toast.info('Apos finalizar a compra, volte para validar e ganhar pontos!');
    setTimeout(() => navigate('/dashboard'), 2000);
  };

  const handleShareWithFriend = () => {
    if (!coupon) return;
    const message = `Olha esse cupom incrivel no ClubdoCupom!

*${coupon.title}*
Desconto: *${coupon.discount_percentage}%*
Categoria: ${coupon.category}

Acesse: www.clubdocupom.com`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCopyCode = () => {
    if (claimData) {
      navigator.clipboard.writeText(claimData.code);
      toast.success('Codigo copiado!');
    }
  };

  const nextImage = () => { if (coupon?.images?.length > 0) setCurrentImageIndex((prev) => (prev + 1) % coupon.images.length); };
  const prevImage = () => { if (coupon?.images?.length > 0) setCurrentImageIndex((prev) => (prev - 1 + coupon.images.length) % coupon.images.length); };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-slate-500"></div>
          <p className="mt-4 text-slate-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!coupon) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="hero-card"><CardContent className="py-12 text-center">
          <p className="text-slate-200">Cupom nao encontrado</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">Voltar</Button>
        </CardContent></Card>
      </div>
    );
  }

  const isSingleUse = coupon.max_uses === 1;
  const merchantName = coupon.merchant_name || coupon.title;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
        </Button>

        <Card data-testid="coupon-details-card" className="hero-card">
          <CardHeader>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Badge className="bg-white/20 text-white border-white/30 text-lg px-4 py-1">
                    {coupon.discount_percentage}% OFF
                  </Badge>
                  {isSingleUse && (
                    <Badge className="bg-red-500/80 text-white border-transparent">Uso Unico</Badge>
                  )}
                </div>
                <CardTitle className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-white">
                  {coupon.title}
                </CardTitle>
                <p className="text-slate-200">por {coupon.merchant_name}</p>
              </div>
            </div>

            {coupon.images && coupon.images.length > 0 && (
              <div className="relative mb-6">
                <div className="relative w-full aspect-[4/3] bg-slate-100 rounded-md overflow-hidden">
                  <img src={coupon.images[currentImageIndex]} alt={`${coupon.title} - imagem ${currentImageIndex + 1}`} className="w-full h-full object-cover" />
                  {coupon.images.length > 1 && (
                    <>
                      <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors" data-testid="prev-image-btn">
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors" data-testid="next-image-btn">
                        <ChevronRight className="h-6 w-6" />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {coupon.images.map((_, idx) => (
                          <button key={idx} onClick={() => setCurrentImageIndex(idx)}
                            className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'}`} data-testid={`image-indicator-${idx}`} />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Descricao</h3>
              <p className="text-slate-200 leading-relaxed">{coupon.description}</p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white mb-3">Detalhes</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-slate-200"><Tag className="h-5 w-5 text-white" /><span><strong>Categoria:</strong> {coupon.category}</span></div>
                <div className="flex items-center gap-3 text-slate-200"><MapPin className="h-5 w-5 text-white" /><span><strong>Localizacao:</strong> {coupon.city}, {coupon.state}</span></div>
                <div className="flex items-center gap-3 text-slate-200"><Calendar className="h-5 w-5 text-white" /><span><strong>Valido ate:</strong> {new Date(coupon.expires_at).toLocaleDateString('pt-BR')}</span></div>
                <div className="flex items-center gap-3 text-slate-200"><Phone className="h-5 w-5 text-white" /><span><strong>Contato:</strong> {coupon.merchant_whatsapp}</span></div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white mb-2">Produtos Incluidos</h3>
              <div className="flex flex-wrap gap-2">
                {coupon.products.map((product, idx) => (
                  <Badge key={idx} className="bg-white/20 text-white border-white/30">{product}</Badge>
                ))}
              </div>
            </div>

            {isSingleUse && (
              <div className="bg-red-500/20 border border-red-400/30 rounded-md p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-300 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-white mb-1">Cupom de Uso Unico</h4>
                  <p className="text-sm text-slate-200">Este cupom so pode ser utilizado uma vez. Apos a ativacao, nenhum outro usuario podera utiliza-lo.</p>
                </div>
              </div>
            )}

            <div className="bg-white/10 border border-white/20 rounded-md p-4">
              <h3 className="font-bold text-white mb-2">Como usar:</h3>
              <ol className="text-sm text-slate-200 space-y-1 list-decimal list-inside">
                <li>Clique em "Ativar Cupom" abaixo</li>
                <li>Receba o codigo e contato do lojista no seu WhatsApp</li>
                <li>Entre em contato com o lojista e finalize a compra</li>
                <li>Volte ao app e valide sua compra para ganhar pontos!</li>
              </ol>
            </div>

            <div className="space-y-3">
              <Button onClick={handleClaim} disabled={claiming}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 text-lg border-0 shadow-lg" data-testid="claim-coupon-btn">
                {claiming ? 'Ativando...' : 'Ativar Cupom'}
              </Button>

              <Button onClick={handleShareWithFriend}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 text-lg border-0 shadow-lg" data-testid="share-friend-btn">
                <WhatsAppIcon className="h-5 w-5 mr-2" /> Enviar no WhatsApp para um Amigo
              </Button>

              <Button variant="outline" onClick={() => navigate('/dashboard')}
                className="w-full border-white/30 text-white hover:bg-white/10 py-4 text-lg" data-testid="view-coupons-btn">
                Ver Meus Cupons Ativos
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Capture Modal */}
        <Dialog open={showWhatsAppCapture} onOpenChange={setShowWhatsAppCapture}>
          <DialogContent className="!bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <WhatsAppIcon className="h-6 w-6 text-green-600" /> Quase la!
              </DialogTitle>
              <DialogDescription className="text-slate-600 mt-2 leading-relaxed">
                Para onde enviamos seu cupom? Cadastre seu celular para receber o codigo via WhatsApp agora mesmo e garantir sua economia na <strong>{merchantName}</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                type="tel"
                placeholder="+5511999999999"
                value={captureWhatsApp}
                onChange={e => setCaptureWhatsApp(e.target.value)}
                className="text-lg py-3"
                data-testid="capture-whatsapp-input"
              />
              <p className="text-xs text-slate-500">Formato: +55 + DDD + numero</p>
              <Button onClick={handleSaveWhatsApp} disabled={savingWhatsApp}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 text-lg"
                data-testid="save-whatsapp-btn">
                <WhatsAppIcon className="h-5 w-5 mr-2" />
                {savingWhatsApp ? 'Salvando...' : 'Cadastrar e Ativar Cupom'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Coupon Claimed Dialog */}
        <Dialog open={showWhatsAppDialog} onOpenChange={setShowWhatsAppDialog}>
          <DialogContent className="!bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <MessageCircle className="h-6 w-6 text-green-600" /> Cupom Ativado!
              </DialogTitle>
              <DialogDescription className="text-slate-600">Use o codigo abaixo para obter seu desconto</DialogDescription>
            </DialogHeader>
            {claimData && (
              <div className="space-y-4 mt-4">
                {isSingleUse && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">Este e um cupom de uso unico. Use-o com sabedoria!</p>
                  </div>
                )}

                <div className="bg-slate-100 border-2 border-slate-300 rounded-md p-4 text-center">
                  <p className="text-xs text-slate-600 mb-1">Seu codigo:</p>
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-2xl font-mono font-bold text-slate-900">{claimData.code}</p>
                    <Button size="sm" variant="ghost" onClick={handleCopyCode} className="h-8 w-8 p-0" data-testid="copy-code-btn">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Valido ate: {new Date(claimData.expires_at).toLocaleDateString('pt-BR')}</p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <h3 className="font-bold text-slate-900 mb-2">{coupon?.title}</h3>
                  <p className="text-sm text-slate-700 mb-2">{coupon?.description}</p>
                  <Badge className="bg-green-600 text-white">{coupon?.discount_percentage}% OFF</Badge>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <h4 className="font-bold text-slate-900 mb-2">Proximos passos:</h4>
                  <ol className="text-sm text-slate-700 space-y-1 list-decimal list-inside">
                    <li>Clique no botao abaixo para abrir o WhatsApp</li>
                    <li>Entre em contato com o lojista</li>
                    <li>Informe o codigo do cupom</li>
                    <li>Finalize sua compra</li>
                    <li>Volte ao app para validar e ganhar pontos!</li>
                  </ol>
                </div>

                <Button onClick={handleOpenWhatsApp} className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 text-lg" data-testid="open-whatsapp-btn">
                  <MessageCircle className="h-5 w-5 mr-2" /> Abrir WhatsApp do Lojista
                </Button>

                <Button variant="outline" onClick={() => { setShowWhatsAppDialog(false); navigate('/dashboard'); }}
                  className="w-full border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="later-btn">
                  Ver Meus Cupons Ativos
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
